import ReactGA from 'react-ga4';
import { gAnalytics } from '../constants/Constants';

let isGAInitialized = false;
const isDevelopment = process.env.NODE_ENV === 'development';

const formatEventName = (eventKey: string): string => {
  if (!eventKey) return 'Interaction';

  return eventKey
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const initializeGAWithConsent = () => {
  if (isGAInitialized) {
    return;
  }

  if (typeof (window as any).CookieConsent !== 'undefined') {
    const analyticsCategory = (window as any).CookieConsent.config.categories.analytics;

    if (analyticsCategory && analyticsCategory.wanted === true) {
      const gaTrackingId = gAnalytics.googleAnalyticsTrackingId;
      ReactGA.initialize(gaTrackingId, {
        gtagOptions: {
          debug_mode: isDevelopment,
          send_page_view: false,
        },
      });

      isGAInitialized = true;

      if (isDevelopment) {
        console.log('✅ GA4 inicializado con consentimiento');
      }
    }
  }
};

export const sendGAEvent = (eventData: Record<string, any>) => {
  if (typeof (window as any).CookieConsent !== 'undefined') {
    const analyticsCategory = (window as any).CookieConsent.config.categories.analytics;

    if (analyticsCategory && analyticsCategory.wanted === true) {
      if (!isGAInitialized) {
        initializeGAWithConsent();
      }

      if (isGAInitialized) {
        const { category, action, label, value, ...rest } = eventData;

        const eventNameKey = action || eventData.event_name || 'interaction';
        const eventName = formatEventName(eventNameKey as string);

        const eventParams = {
          event_category: category,
          event_label: label,
          value: value,
          ...rest,
        };

        Object.keys(eventParams).forEach(key => eventParams[key] === undefined && delete eventParams[key]);

        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', eventName, eventParams);
        } else {
          ReactGA.event({
            action: eventName,
            category,
            label,
            value,
            ...rest,
          });
        }

        if (isDevelopment) {
          console.log(`📊 Evento: "${eventName}"`, {
            category,
            label,
            ...rest,
          });
        }
      }
    }
  }
};

export const sendGAPageview = (pageData: any) => {
  if (typeof (window as any).CookieConsent !== 'undefined') {
    const analyticsCategory = (window as any).CookieConsent.config.categories.analytics;

    if (analyticsCategory && analyticsCategory.wanted === true) {
      if (!isGAInitialized) {
        initializeGAWithConsent();
      }

      if (isGAInitialized) {
        ReactGA.send(pageData);

        if (isDevelopment) {
          console.log('📄 Page view enviado', pageData);
        }
      }
    }
  }
};

export const hasAnalyticsConsent = (): boolean => {
  if (typeof (window as any).CookieConsent !== 'undefined') {
    const analyticsCategory = (window as any).CookieConsent.config.categories.analytics;
    return analyticsCategory && analyticsCategory.wanted === true;
  }
  return false;
};

export const setupConsentListener = (userId: string | null = null) => {
  const checkAndSetupListener = () => {
    if (typeof (window as any).CookieConsent !== 'undefined') {
      const analyticsCategory = (window as any).CookieConsent.config.categories.analytics;

      if (analyticsCategory && analyticsCategory.wanted === true) {
        initializeGAWithConsent();
      }

      document.addEventListener('click', event => {
        const target = event.target as HTMLElement;

        if (
          target.classList.contains('consent-give') ||
          target.classList.contains('ccm__footer') ||
          target.textContent?.includes('Accept all cookies') ||
          target.textContent?.includes('Save current settings')
        ) {
          setTimeout(() => {
            initializeGAWithConsent();

            if (userId && typeof (window as any).syncCookieConsentWithCognito === 'function') {
              const cookiePreferences = {
                categories: (window as any).CookieConsent.config.categories,
              };
              (window as any).syncCookieConsentWithCognito(userId, cookiePreferences);
            }

            if (isDevelopment) {
              console.log('🔄 Consentimiento actualizado y sincronizado');
            }
          }, 200);
        }
      });
    } else {
      setTimeout(checkAndSetupListener, 100);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAndSetupListener);
  } else {
    checkAndSetupListener();
  }
};

export const clearConsentCookies = () => {
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    if (name.includes('consent') || name.includes('cookie') || name.includes('_ga')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    }
  });

  Object.keys(localStorage).forEach(key => {
    if (key.includes('consent') || key.includes('cookie')) {
      localStorage.removeItem(key);
    }
  });

  if (isDevelopment) {
    console.log('🗑️ Cookies de consentimiento eliminadas');
  }
};

if (typeof window !== 'undefined') {
  (window as any).clearConsentCookies = clearConsentCookies;
}
